import React, { useCallback, useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import Actions from '../../../actions'
import $t from '../../../i18n'
import Page from '../../common/Page/Page'
import PageCard from '../../common/PageCard'
import config from '../../../config.json'
import SSOAlert from '../../common/SSOAlert'
import { BenefitTypeNames } from 'consts/coverageBenefitsConsts'
import { hasChildren, getMemberNameById } from 'utils/familyUtils'
import dayjs from 'dayjs'
import { ACCUMULATOR_TYPES, MEMBER_TYPES } from 'consts/coverageBenefitsConsts'

import './CoverageBenefits.css'

import CopayCard from './CopayCard'
import BenefitLinkCard from './BenefitLinkCard'
import DocumentsCard from './DocumentsCard'
import BenefitsCard from './BenefitsCard'

const CoverageBenefits = ({ subscription }) => {
  const dispatch = useDispatch()
  const [isSSONoteVisible, setIsSSONoteVisible] = useState(false)
  const [ssoLinkName, setSSOLinkName] = useState('')
  const [ssoLink, setSSOLink] = useState('')
  const [membersAccumulatorsMap, setMembersAccumulatorsMap] = useState(
    new Map()
  )
  const members = useSelector(state => state.account?.members)
  const membersAcrossPolicies = useSelector(
    state => state.account?.membersAcrossPolicies
  )
  const selectedMember = useSelector(state => state.account?.selectedMember)
  const userDetails = useSelector(state => state.profile?.userDetails)
  const cardHolderMemberId = userDetails.hmo_member_id
  const effectiveStartDate = userDetails.hmo_effective_begin_date
  const effectiveSubscriptionStartDate = subscription?.current_period_start
  const accumulators = useSelector(state => state.profile.membersAccumulators)

  const planName = subscription?.plan_name
  const isDentalIncluded = !!subscription?.plan_details.is_adult_dental_hmo_plan

  const hasDependentsUnder18 = hasChildren(members)

  // A member's usage of null means "unknown", not zero. Summing family usage from a mix of
  // known and unknown members would silently show a partial sum as if it were the complete
  // total, so any unknown member makes the family figure unknown too.
  const sumOrUnknown = values =>
    values.some(value => value == null)
      ? null
      : values.reduce((total, value) => total + value, 0)

  const toAmount = value => (value == null ? null : Number(value.toFixed(2)))

  // Reducer function to sum up the amounts in the accumulator response array
  const sumAccumulators = accumulators => {
    return {
      deductibleUsage: sumOrUnknown(
        accumulators.map(curr => curr?.deductibleUsage)
      ),
      oopUsage: sumOrUnknown(accumulators.map(curr => curr?.oopUsage)),
      deductibleMaxValue: accumulators.reduce(
        (total, curr) => total + (curr?.deductibleMaxValue || 0),
        0
      ),
      oopMaxValue: accumulators.reduce(
        (total, curr) => total + (curr?.oopMaxValue || 0),
        0
      )
    }
  }

  const buildAccumulatorsMap = useCallback(
    accumulators => {
      if (!accumulators) return null

      const family = membersAcrossPolicies?.concat(userDetails)
      const accumulatorMap = new Map()

      accumulators.forEach(member => {
        accumulatorMap.set(member.memberId, {
          name: getMemberNameById(family, member.memberId),
          [ACCUMULATOR_TYPES.DEDUCTIBLE]: {
            // null means "no record for this member" and must stay distinguishable from a
            // real $0 of usage, so it is not coerced here — BenefitsCard renders it.
            usage: toAmount(member.deductibleUsage),
            max: toAmount(member.deductibleMaxValue ?? 0)
          },
          [ACCUMULATOR_TYPES.OOP]: {
            usage: toAmount(member.oopUsage),
            max: toAmount(member.oopMaxValue ?? 0)
          }
        })
      })

      // Cognizant already settles the four family fields onto every row. Solidarity
      // omits them, so we still sum per-member shares for that provider.
      const familyAware = accumulators.find(
        member => 'familyDeductibleMaxValue' in member
      )
      const familyTotals = familyAware
        ? {
            deductibleUsage: familyAware.familyDeductibleUsage,
            oopUsage: familyAware.familyOopUsage,
            deductibleMaxValue: familyAware.familyDeductibleMaxValue,
            oopMaxValue: familyAware.familyOopMaxValue
          }
        : sumAccumulators(accumulators)

      accumulatorMap.set(MEMBER_TYPES.FAMILY, {
        [ACCUMULATOR_TYPES.DEDUCTIBLE]: {
          usage: toAmount(familyTotals.deductibleUsage),
          max: toAmount(familyTotals.deductibleMaxValue)
        },
        [ACCUMULATOR_TYPES.OOP]: {
          usage: toAmount(familyTotals.oopUsage),
          max: toAmount(familyTotals.oopMaxValue)
        }
      })

      setMembersAccumulatorsMap(accumulatorMap)
    },
    [members, userDetails]
  )

  useEffect(() => {
    const memberIds = [cardHolderMemberId]
      .concat(members?.map(member => member.hmo_member_id))
      .filter(id => id !== null)

    dispatch(
      Actions.createAction(Actions.MEMBERS_ACCUMULATORS_REQUESTED, {
        memberIds,
        year: dayjs(effectiveSubscriptionStartDate).year()
      })
    )
  }, [cardHolderMemberId, members, effectiveSubscriptionStartDate, dispatch])

  useEffect(() => {
    if (accumulators) {
      buildAccumulatorsMap(accumulators)
    }
  }, [buildAccumulatorsMap, accumulators])

  const redirectToDentalBenefitPortal = link => {
    window.open(link, '_blank')
  }

  const redirectToMedicationBenefitPortal = link => {
    setSSOLinkName(BenefitTypeNames.MEDICATION)
    setSSOLink(link)
    setIsSSONoteVisible(true)
  }

  const redirectToVisionBenefitPortal = link => {
    setSSOLinkName(BenefitTypeNames.VISION)
    setSSOLink(link)
    setIsSSONoteVisible(true)
  }

  return (
    <PageCard enableBack={true}>
      <Page
        fullWidth={true}
        hideAlertBanner
        className='coverage-benefits-page'
        title={$t('pages.coverageBenefits.title')}
      >
        <div className='benefits-container'>
          <div className='plan-details-container'>
            {planName && (
              <div>
                {$t('pages.coverageBenefits.plan', {
                  plan_name: planName
                })}
              </div>
            )}
            <div>
              {$t('pages.coverageBenefits.effectiveDate', {
                effective_date: dayjs(
                  effectiveSubscriptionStartDate || effectiveStartDate
                ).format(config.genericDateFormat)
              })}
            </div>
          </div>
          <BenefitsCard
            subscription={subscription}
            accumulatorsMap={membersAccumulatorsMap}
            isFamily={members?.length > 1}
          />
          <CopayCard subscription={subscription} />
          <BenefitLinkCard
            type={BenefitTypeNames.MEDICATION}
            onClick={redirectToMedicationBenefitPortal}
          />
          {hasDependentsUnder18 && (
            <BenefitLinkCard
              type={BenefitTypeNames.VISION}
              onClick={redirectToVisionBenefitPortal}
            />
          )}
          {(isDentalIncluded || hasDependentsUnder18) && (
            <BenefitLinkCard
              type={BenefitTypeNames.DENTAL}
              onClick={redirectToDentalBenefitPortal}
            />
          )}
          <DocumentsCard
            subscription={subscription}
            member={selectedMember ?? userDetails}
          />
        </div>
      </Page>
      <SSOAlert
        isSSONoteVisible={isSSONoteVisible}
        setIsSSONoteVisible={setIsSSONoteVisible}
        ssoLink={ssoLink}
        ssoLinkName={ssoLinkName}
      />
    </PageCard>
  )
}

export default CoverageBenefits
